import { useCallback, useContext, useMemo } from 'react';

import { Image } from '@swyftx/aviary/atoms/Image/Image';
import { TourGuideContext } from '@swyftx/aviary/molecules/TourGuide';

import { FirstSteps } from '@shared/api';
import { Links } from '@shared/constants';

import { AppFeature, AppFeatureData, useIsFeatureEnabled } from 'src/config';
import { useAutoInvestAnalytics } from 'src/lib/auto-invest/hooks';
import { useSetTwoFactorAuthenticationTour } from 'src/lib/feature-discovery/tours/profile/security/setTwoFactorAuthentication.tour';
import { useNavigateRoute } from 'src/lib/navigation/hooks';
import { NavigationURLs } from 'src/lib/navigation/types';

import { useResources } from './useResources';

export type GettingStartedCardType = {
  icon: React.ReactElement;
  id: FirstSteps;
  title: string;
  content: string;
  hidden?: boolean;
  onClick: () => void;
};

export function useCards() {
  const { firstSteps } = useResources();
  const { navigate } = useNavigateRoute();
  const { startTour } = useContext(TourGuideContext);
  const twoFactorTour = useSetTwoFactorAuthenticationTour();
  const { isFeatureEnabled, getFeatureData } = useIsFeatureEnabled();
  const { gettingStartedTileOpened } = useAutoInvestAnalytics();
  const { amount, equivalentCurrencyName, localCurrencyCode } = getFeatureData(
    AppFeatureData.RefereeBannerReferralBonusAmount,
  );

  const openLink = (url: string) => {
    if (!url) return;

    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openAutoInvestTile = useCallback(() => {
    gettingStartedTileOpened();
    navigate(NavigationURLs.AutoInvest);
  }, [gettingStartedTileOpened, navigate]);

  const referAFriendContent = useMemo(
    () =>
      `Invite friends & family to trade with Swyftx within 30 days of signup and you'll both score $${
        amount ? amount : '10'
      } ${localCurrencyCode ? localCurrencyCode : 'AUD'} of ${
        equivalentCurrencyName ? equivalentCurrencyName : 'Bitcoin'
      }`,
    [amount, localCurrencyCode, equivalentCurrencyName],
  );

  const items = useMemo(() => {
    const cardRecord: Record<FirstSteps, GettingStartedCardType> = {
      CompleteIDAuthentication: {
        icon: <Image image='id_reviewing' usePalette className='h-64 w-64' />,
        title: 'Complete ID Authentication',
        content: 'Authenticate your ID to unlock additional features such as crypto withdrawals and deposits.',
        onClick: () => navigate(NavigationURLs.ProfileVerification),
        id: FirstSteps.CompleteIDAuthentication,
      },
      SecureYourAccount: {
        icon: <Image image='secure_account' usePalette className='h-64 w-64' />,
        title: 'Secure your account',
        content: 'Add another layer of security to your account by enabling 2-factor authentication.',
        onClick: () => startTour(twoFactorTour),
        id: FirstSteps.SecureYourAccount,
      },
      SetPriceAlert: {
        icon: <Image image='price_alerts' usePalette className='h-64 w-64' />,
        title: 'Set a price alert',
        content: 'Never miss a market movement with customer price alerts on your selected assets.',
        onClick: () => navigate(NavigationURLs.PriceAlerts),
        id: FirstSteps.SetPriceAlert,
      },
      CreateRecurringOrder: {
        icon: <Image image='auto_invest' className='h-64 w-64' />,
        title: 'Create a recurring order',
        hidden: !isFeatureEnabled(AppFeature.AutoInvest),
        content:
          'Create orders that execute on a routine schedule and automate the purchase process for multiple assets.',
        onClick: openAutoInvestTile,
        id: FirstSteps.CreateRecurringOrder,
      },
      SwyftxLearn: {
        icon: <Image image='learn' usePalette className='h-64 w-64' />,
        title: 'Swyftx Learn',
        content:
          'Access quality educational resources and expert market analysis to sharpen your crypto and blockchain knowledge',
        onClick: () => openLink(Links.learn.root),
        id: FirstSteps.SwyftxLearn,
      },
      ReferFriend: {
        icon: <Image image='refer_a_friend' usePalette className='h-64 w-64' />,
        title: 'Refer a friend',
        hidden: isFeatureEnabled(AppFeature.AutoInvest),
        content: referAFriendContent,
        onClick: () => navigate(NavigationURLs.ProfileReferAFriend),
        id: FirstSteps.ReferFriend,
      },
    };

    return firstSteps?.map((stepId) => cardRecord[stepId]).filter((card) => !card.hidden) || [];
  }, [isFeatureEnabled, openAutoInvestTile, referAFriendContent, firstSteps, startTour, twoFactorTour, navigate]);

  const hasItems = useMemo(() => items.length > 0, [items]);

  return {
    items,
    hasItems,
  };
}
